import {
  APICallState,
  MFAActions,
  MFAChannel,
  PreferenceSelectorState,
  Selectors,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {useCallback, useState} from 'react';
import {ROUTES} from 'routes';

import AddContactInfo from 'components/AddContactInfo';
import ErrorPanel from 'components/ErrorPanel';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';

const ConnectedAddContactInfo: React.FunctionComponent<{
  channel: MFAChannel;
}> = ({channel}) => {
  const dispatch = useWidgetDispatch();
  const navigate = useNavigateWithParams();
  const editProfileState = useWidgetState(Selectors.editProfileStateSelector);
  const preferenceSelectorState = useWidgetState(
    Selectors.preferenceSelectorStateSelector,
  ) as PreferenceSelectorState;
  const profileLoadState = useWidgetState(Selectors.mfaProfileStatusSelector);
  const securityEmail = useWidgetState(Selectors.securityEmailSelector) as
    | string
    | undefined;
  const securityPhone = useWidgetState(
    Selectors.securityMobilePhoneSelector,
  ) as string | undefined;

  /**
   * Capture the input value in order to re-use it
   */
  const [contactInfoValue, setContactInfoValue] = useState('');

  /**
   * If the user input matches the value on file
   */
  const [isDuplicate, setIsDuplicate] = useState(false);

  const onSend = useCallback(
    (value: string) => {
      // Check if the value is duplicate
      if ([securityEmail, securityPhone].includes(value)) {
        return setIsDuplicate(true);
      }

      setContactInfoValue(value);

      const action =
        channel === MFAChannel.EMAIL
          ? // Passing isUpdate = true here to set the operation as update on the API for editSecurityProfile
            // This is because create operation does not require verifying the email / sms,
            // as it is used for MFA onboarding, but when adding a new contact info we want the contact to be verified
            MFAActions.editSecurityEmail({securityEmail: value, isUpdate: true})
          : MFAActions.editSecurityPhone({
              securityPhone: value,
              isUpdate: true,
            });

      dispatch(action);
    },
    [channel, dispatch, securityEmail, securityPhone],
  );

  const clearDuplicateError = () => setIsDuplicate(false);

  if (editProfileState === APICallState.SUCCESS) {
    dispatch(MFAActions.resetEditSecurityProfile());
    navigate(ROUTES.sendCodeSuccess, {state: {channel, contactInfoValue}});
    return null;
  }

  if (editProfileState === APICallState.ERROR) {
    return (
      <ErrorPanel
        /**
         * the email address that was entered by the user gets cleared when we render
         * an ErrorPanel so passing onSendEmail to the retry button would make the api
         * call with no email.
         */
        onRetryPress={() => dispatch(MFAActions.resetEditSecurityProfile())}
        onCancelPress={undefined}
      />
    );
  }

  return (
    <AddContactInfo
      onSend={onSend}
      preferenceSelectorState={preferenceSelectorState}
      isLoading={[editProfileState, profileLoadState].includes(
        APICallState.LOADING,
      )}
      channel={channel}
      isDuplicate={isDuplicate}
      clearDuplicateError={clearDuplicateError}
    />
  );
};

export default ConnectedAddContactInfo;
